import { ProductPageTemplate } from "@/components/ProductPageTemplate";
import { Shield, Package, AlertTriangle, Scale, Users, FileText } from "lucide-react";
import {getImageUrl} from '@/lib/utils';

const ProductLiability = () => {
  const pageData = {
    // SEO Meta Data
    title: "Product Liability Insurance UAE | Manufacturing Insurance Dubai | YallaCompare",
    metaDescription: "Get product liability insurance in UAE. Protect your business from product defect claims in Dubai, Abu Dhabi & UAE. Compare manufacturing insurance quotes.",

    // Hero Section
    heroTitle: "Product Liability Insurance for UAE Businesses",
    heroSubtitle: "Product Safety Protection",
    heroDescription: "Protect your business from claims related to product defects, recalls, and consumer injuries. Essential coverage for manufacturers, distributors, and retailers in the UAE.",
    heroImage: getImageUrl("product-liability-icon.png"),

    // Key Benefits
    keyBenefits: [
      "Protection against product defect claims",
      "Coverage for consumer injury lawsuits",
      "Product recall expense coverage",
      "Legal defense and settlement costs",
      "Worldwide coverage for exported products",
      "Reputation and brand protection"
    ],

    // Features Section
    features: [
      {
        title: "Product Defect Coverage",
        description: "Protection against claims arising from defective products that cause injury or damage.",
        icon: <Package className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Legal Defense",
        description: "Comprehensive legal defense coverage for product liability lawsuits and claims.",
        icon: <Scale className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Recall Protection",
        description: "Coverage for costs associated with product recalls and safety notifications.",
        icon: <AlertTriangle className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Consumer Safety",
        description: "Protection against consumer injury claims and property damage lawsuits.",
        icon: <Users className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Brand Protection",
        description: "Safeguard your brand reputation and business relationships from product claims.",
        icon: <Shield className="h-6 w-6 text-blue-600" />
      },
      {
        title: "Regulatory Compliance",
        description: "Meet UAE product safety regulations and international export requirements.",
        icon: <FileText className="h-6 w-6 text-blue-600" />
      }
    ],

    // Coverage Details
    coverageDetails: [
      {
        title: "Product Liability Protection",
        description: "Comprehensive coverage for product-related claims and lawsuits",
        included: [
          "Product defect claims",
          "Consumer injury lawsuits",
          "Property damage from products",
          "Design and manufacturing defects",
          "Inadequate warnings or instructions"
        ]
      },
      {
        title: "Legal and Recall Coverage",
        description: "Protection for legal costs and product recall expenses",
        included: [
          "Legal defense costs",
          "Settlement and judgment payments",
          "Product recall expenses",
          "Crisis management costs",
          "Regulatory investigation costs"
        ]
      }
    ],

    // Why Choose Us
    whyChooseUs: [
      {
        title: "Financial Protection",
        description: "Protect your business from potentially devastating product liability claims and lawsuits"
      },
      {
        title: "Global Coverage",
        description: "Worldwide protection for products sold domestically and internationally"
      },
      {
        title: "Regulatory Compliance",
        description: "Meet UAE and international product safety and liability requirements"
      },
      {
        title: "Business Continuity",
        description: "Ensure your business can continue operating despite product liability challenges"
      }
    ],

    // FAQ Section
    faqs: [
      {
        question: "Who needs product liability insurance in the UAE?",
        answer: "Manufacturers, distributors, retailers, importers, and any business that sells or supplies products to consumers should consider product liability insurance."
      },
      {
        question: "What types of claims are covered?",
        answer: "Coverage includes claims for bodily injury, property damage, and economic losses caused by defective products, design flaws, or inadequate warnings."
      },
      {
        question: "Does it cover product recalls?",
        answer: "Yes, many policies include coverage for product recall expenses, including notification costs, transportation, and disposal of recalled products."
      },
      {
        question: "Is coverage available for exported products?",
        answer: "Yes, product liability insurance can provide worldwide coverage for products sold internationally, subject to policy terms."
      },
      {
        question: "How are premiums calculated?",
        answer: "Premiums depend on product type, sales volume, manufacturing processes, claims history, and target markets. Higher-risk products typically have higher premiums."
      }
    ],

    // CTA Section
    ctaTitle: "Protect Your Products and Business Today",
    ctaDescription: "Get comprehensive product liability insurance tailored to your business. Compare quotes from leading UAE insurers and secure your product protection.",
    primaryCta: "Get Product Liability Quote",
    secondaryCta: "Compare All Insurance Products",

    // Product ID for quote form
    productId: "product-liability"
  };

  return <ProductPageTemplate {...pageData} />;
};

export default ProductLiability;
